import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Collapse,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  LocationCity,
  Event,
  LocalOffer,
  Contacts,
  People,
  Category,
  AppRegistration,
  HomeWork,
  Handyman,
  SubdirectoryArrowRight,
  Policy,
  Support,
  Language,
  VerifiedUser,
  AssignmentTurnedIn,
  AssignmentInd,
  ReportProblem,
  StarRate,
  NotificationsActive,
  History,
  Link as LinkIcon,
  QuestionAnswer,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';
import { getAllowedPathsForRole } from '../../utils/roleUtils';

const drawerWidth = 240;

const menuItems = [
  { textKey: 'nav.dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  {
    isDropdown: true,
    key: 'promotionsGroup',
    textKey: 'nav.promotions',
    icon: <LocalOffer />,
    children: [
      { textKey: 'nav.promotions', icon: <LocalOffer />, path: '/promotions' },
      { textKey: 'nav.promotionTypes', icon: <Category />, path: '/promotion-types' },
    ],
  },
  {
    isDropdown: true,
    key: 'contactsGroup',
    textKey: 'nav.contacts',
    icon: <Contacts />,
    children: [
      { textKey: 'nav.contacts', icon: <Contacts />, path: '/contacts' },
      { textKey: 'nav.contactInquiries', icon: <QuestionAnswer />, path: '/contact-inquiries' },
      { textKey: 'nav.providerClaims', icon: <AssignmentTurnedIn />, path: '/provider-claims' },
      { textKey: 'nav.categories', icon: <Category />, path: '/categories' },
      { textKey: 'nav.serviceSubCategories', icon: <SubdirectoryArrowRight />, path: '/service-sub-categories' },
    ],
  },
  {
    isDropdown: true,
    key: 'providerGroup',
    textKey: 'nav.provider',
    icon: <AssignmentInd />,
    children: [
      { textKey: 'nav.providerApplications', icon: <AssignmentInd />, path: '/provider-applications' },
      { textKey: 'nav.providerNotifications', icon: <NotificationsActive />, path: '/provider-custom-notifications' },
      { textKey: 'nav.documentsVerification', icon: <VerifiedUser />, path: '/documents-verification' },
      { textKey: 'nav.customerSupport', icon: <Support />, path: '/customer-support' },
    ],
  },
  {
    isDropdown: true,
    key: 'accommodationGroup',
    textKey: 'nav.accommodation',
    icon: <HomeWork />,
    children: [
      { textKey: 'nav.accommodation', icon: <HomeWork />, path: '/accommodation' },
      { textKey: 'nav.accommodationFacilities', icon: <Handyman />, path: '/accommodation-facilities' },
      { textKey: 'nav.accommodationTypes', icon: <Category />, path: '/accommodation-types' },
    ],
  },
  {
    isDropdown: true,
    key: 'eventsGroup',
    textKey: 'nav.events',
    icon: <Event />,
    children: [
      { textKey: 'nav.events', icon: <Event />, path: '/events' },
      { textKey: 'nav.eventTypes', icon: <Category />, path: '/event-types' },
    ],
  },
  {
    isDropdown: true,
    key: 'citiesGroup',
    textKey: 'nav.cities',
    icon: <LocationCity />,
    children: [
      { textKey: 'nav.cities', icon: <LocationCity />, path: '/cities' },
      { textKey: 'nav.districts', icon: <LocationCity />, path: '/districts' },
      { textKey: 'nav.cityRegistration', icon: <AppRegistration />, path: '/city-registrations' },
    ],
  },
  {
    isDropdown: true,
    key: 'usersGroup',
    textKey: 'nav.users',
    icon: <People />,
    children: [
      { textKey: 'nav.users', icon: <People />, path: '/users' },
      { textKey: 'nav.userActivity', icon: <History />, path: '/user-activity' },
    ],
  },
  { textKey: 'nav.referralLinks', icon: <LinkIcon />, path: '/referral-links' },
  { textKey: 'nav.jobPortals', icon: <Policy />, path: '/job-portals' },
  { textKey: 'nav.languageSupport', icon: <Language />, path: '/language-support' },
  { textKey: 'nav.legalPages', icon: <Policy />, path: '/legal' },
  { textKey: 'nav.userReports', icon: <ReportProblem />, path: '/user-reports' },
  { textKey: 'nav.ratingModules', icon: <StarRate />, path: '/rating-modules' },
  { textKey: 'nav.contactGoogleRating', icon: <StarRate />, path: '/contact-google-rating' },
];

const Sidebar = ({ open, mobile = false, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const adminUser = getAdminUser();
  const isSuperAdmin = /super\s*admin/i.test(adminUser?.role || '');
  const isAdminUser = /admin\s*user/i.test(adminUser?.role || '') || String(adminUser?.role || '').toLowerCase().replace(/\s+/g, '') === 'adminuser';

  const [openDropdowns, setOpenDropdowns] = useState(() => {
    const current = location.pathname;
    return {
      promotionsGroup: current === '/promotions' || current === '/promotion-types',
      contactsGroup: current === '/contacts' || current === '/provider-claims' || current === '/categories' || current === '/service-sub-categories' || current === '/contact-inquiries',
      providerGroup: current === '/provider-applications' || current === '/provider-custom-notifications' || current === '/documents-verification' || current === '/customer-support',
      accommodationGroup: current === '/accommodation' || current === '/accommodation-facilities' || current === '/accommodation-types',
      eventsGroup: current === '/events' || current === '/event-types',
      citiesGroup: current === '/cities' || current === '/districts' || current === '/city-registrations',
      usersGroup: current === '/users' || current === '/user-activity',
    };
  });

  useEffect(() => {
    setOpenDropdowns(prev => {
      const next = { ...prev };
      const current = location.pathname;
      if (current === '/promotions' || current === '/promotion-types') next.promotionsGroup = true;
      if (current === '/contacts' || current === '/provider-claims' || current === '/categories' || current === '/service-sub-categories' || current === '/contact-inquiries') next.contactsGroup = true;
      if (current === '/provider-applications' || current === '/provider-custom-notifications' || current === '/documents-verification' || current === '/customer-support') next.providerGroup = true;
      if (current === '/accommodation' || current === '/accommodation-facilities' || current === '/accommodation-types') next.accommodationGroup = true;
      if (current === '/events' || current === '/event-types') next.eventsGroup = true;
      if (current === '/cities' || current === '/districts' || current === '/city-registrations') next.citiesGroup = true;
      if (current === '/users' || current === '/user-activity') next.usersGroup = true;
      return next;
    });
  }, [location.pathname]);

  const toggleDropdown = (key) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const allowedPaths = getAllowedPathsForRole(adminUser?.role);

  const filteredMenuItems = menuItems.map(item => {
    if (allowedPaths) {
      if (item.isDropdown) {
        const validChildren = item.children.filter(child => allowedPaths.includes(child.path));
        if (validChildren.length === 0) return null;
        return { ...item, children: validChildren };
      }
      return allowedPaths.includes(item.path) ? item : null;
    }
    if (isAdminUser) {
      if (item.isDropdown) return item;
      return item.path === '/referral-links' || item.path === '/contact-google-rating' ? item : null;
    }
    if (item.path === '/legal' && !isSuperAdmin) return null;
    return item;
  }).filter(Boolean);

  const handleClick = (path) => {
    navigate(path);
    if (mobile && onClose) {
      onClose();
    }
  };

  return (
    <Drawer
      variant={mobile ? 'temporary' : 'permanent'}
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
      PaperProps={{
        sx: {
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#328284',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#0F3D3E',
          },
        }
      }}
      sx={{
        width: mobile ? drawerWidth : open ? drawerWidth : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          bgcolor: '#ffffff',
          borderRight: '1px solid #F3F4F6',
          ...(mobile
            ? {
                mt: '64px',
                height: 'calc(100% - 64px)',
              }
            : {
                transform: open ? 'none' : `translateX(-${drawerWidth}px)`,
                transition: (theme) =>
                  theme.transitions.create(['transform'], {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                  }),
              }),
          overflowX: 'hidden',
          overflowY: 'auto',
        },
      }}
    >
      {!mobile && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 2.5,
          px: 3,
          borderBottom: '1px solid #F3F4F6',
          mb: 1.5,
        }}>
          <img
            src={logo}
            alt="Logo"
            style={{
              maxHeight: '42px',
              maxWidth: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>
      )}
      <List
        sx={{
          px: mobile ? 1 : 0.75,
          '& .MuiListItemButton-root': {
            borderRadius: '10px',
            my: 0.25,
            py: 0.85,
            transition: 'all 0.15s ease-in-out',
            color: '#334155',
            '&:hover': {
              bgcolor: '#F0F7F7',
              color: '#0F3D3E',
              '& .MuiListItemIcon-root': {
                color: '#0F3D3E',
                opacity: 1,
              },
            },
            '&.Mui-selected': {
              bgcolor: '#0F3D3E',
              color: '#ffffff',
              boxShadow: '0 2px 8px rgba(15, 61, 62, 0.16)',
              '&:hover': {
                bgcolor: '#0B3031',
              },
              '& .MuiListItemIcon-root': {
                color: '#ffffff',
                opacity: 1,
              },
              '& .MuiTypography-root': {
                fontWeight: 700,
                color: '#ffffff',
              },
            },
          },
          '& .MuiListItemIcon-root': {
            color: '#0F3D3E',
            opacity: 0.85,
            minWidth: 36,
            transition: 'color 0.15s ease-in-out',
          },
          '& .MuiTypography-root': {
            fontSize: '0.88rem',
            fontWeight: 500,
          },
        }}
      >
        {filteredMenuItems.map((item) => {
          if (item.isDropdown) {
            const isChildActive = item.children.some(child => child.path === location.pathname);
            const isOpen = !!openDropdowns[item.key];

            return (
              <React.Fragment key={item.key}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleDropdown(item.key)}
                    selected={isChildActive && !isOpen}
                    sx={{
                      borderRadius: '10px',
                      my: 0.25,
                      bgcolor: isChildActive && !isOpen ? '#F0F7F7' : 'transparent',
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={t(item.textKey)}
                      primaryTypographyProps={{
                        fontWeight: (isChildActive || isOpen) ? 700 : 500,
                        color: (isChildActive || isOpen) ? '#0F3D3E' : 'inherit',
                      }}
                    />
                    {isOpen ? (
                      <ExpandLess sx={{ color: '#0F3D3E', fontSize: 20 }} />
                    ) : (
                      <ExpandMore sx={{ color: '#64748B', fontSize: 20 }} />
                    )}
                  </ListItemButton>
                </ListItem>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 1.5, mt: 0.25, mb: 0.5 }}>
                    {item.children.map((child) => {
                      const isSelected = location.pathname === child.path;
                      return (
                        <ListItem key={child.path} disablePadding>
                          <ListItemButton
                            onClick={() => handleClick(child.path)}
                            selected={isSelected}
                            sx={{
                              borderRadius: '8px',
                              my: 0.25,
                              pl: 2,
                              py: 0.7,
                              borderLeft: isSelected ? '3px solid #0F3D3E' : '3px solid transparent',
                              bgcolor: isSelected ? '#0F3D3E' : 'transparent',
                              '&:hover': {
                                bgcolor: isSelected ? '#0B3031' : '#F0F7F7',
                                color: isSelected ? '#ffffff' : '#0F3D3E',
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 28 }}>{child.icon}</ListItemIcon>
                            <ListItemText
                              primary={t(child.textKey)}
                              primaryTypographyProps={{
                                fontSize: '0.83rem',
                                fontWeight: isSelected ? 700 : 500,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => handleClick(item.path)}
                selected={location.pathname === item.path}
                sx={{ borderRadius: '10px', my: 0.25 }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={t(item.textKey)} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Drawer>
  );
};

Sidebar.propTypes = {
  open: PropTypes.bool.isRequired,
  mobile: PropTypes.bool,
  onClose: PropTypes.func,
};

export default Sidebar;
